import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ChevronDown } from "lucide-react";
import { scrollTo } from "@/components/Header";
import { useLang } from "@/i18n";

const Line = ({ children, delay, className = "" }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const ref = useRef(null);
  const { lang, t } = useLang();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const videoY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const videoScale = useTransform(scrollYProgress, [0, 1], [1, 1.18]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} id="top" className="relative h-screen min-h-[680px] overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y: videoY, scale: videoScale }} className="absolute inset-0">
        <video
          className="w-full h-full object-cover"
          src="/assets/hero-video.mp4"
          poster="/assets/parabola.webp"
          autoPlay loop muted playsInline
          data-testid="hero-video"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-anthracite via-anthracite/55 to-anthracite/30" />
      <div className="absolute inset-0 bg-gradient-to-r from-anthracite/70 to-transparent" />

      <motion.div style={{ opacity: fade }} className="relative z-10 h-full max-w-7xl mx-auto px-6 flex flex-col justify-end pb-24">
        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1, duration: 0.8 }}
          className="overline-tag mb-6" data-testid="hero-overline"
        >
          {t.hero.overline}
        </motion.p>
        <h1 key={lang} className="font-display font-bold tracking-tighter leading-[0.98] text-4xl sm:text-6xl lg:text-7xl max-w-4xl" data-testid="hero-headline">
          <Line delay={0.25}>{t.hero.line1}</Line>
          <Line delay={0.4}>{t.hero.line2}</Line>
          <Line delay={0.6} className="text-volt text-2xl sm:text-3xl lg:text-4xl mt-5 tracking-tight">
            {t.hero.line3}
          </Line>
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.8 }}
          className="mt-6 max-w-xl text-steel text-base sm:text-lg leading-relaxed" data-testid="hero-subtitle"
        >
          <span className="text-white font-semibold">{t.hero.brand}</span> {t.hero.subtitle}
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 0.8 }}
          className="mt-9 flex flex-wrap gap-4"
        >
          <button onClick={() => scrollTo("#contatti")} className="btn-primary" data-testid="hero-cta-simulation">
            {t.hero.cta1} <ArrowRight size={16} />
          </button>
        </motion.div>
      </motion.div>

      <motion.button
        onClick={() => scrollTo("#problema")}
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6 }}
        className="absolute bottom-8 right-8 z-10 text-steel hover:text-volt transition-colors"
        data-testid="hero-scroll-indicator" aria-label="Scroll"
      >
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2 }} className="block">
          <ChevronDown size={26} />
        </motion.span>
      </motion.button>
    </section>
  );
}
